import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateCallAnalysisTagDefinitionRequest,
  UpdateCallAnalysisTagDefinitionRequest,
} from '@reach/shared-types/call-analysis-tag-definitions';
import { useToast } from '../../../hooks/useToast';
import {
  archiveTagDefinition,
  createTagDefinition,
  fetchTagDefinitions,
  updateTagDefinition,
} from './call-analysis-tags-api';

const TAG_DEFS_KEY = ['callAnalysisTagDefinitions'] as const;

export function useCallAnalysisTagDefinitions() {
  return useQuery({
    queryKey: TAG_DEFS_KEY,
    queryFn: () => fetchTagDefinitions(),
    staleTime: 60_000,
  });
}

export function useCreateTagDefinition() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (body: CreateCallAnalysisTagDefinitionRequest) => createTagDefinition(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAG_DEFS_KEY });
      addToast({ title: 'Tag created', variant: 'default' });
    },
    onError: (error: Error) => {
      addToast({
        title: 'Failed to create tag',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateTagDefinition() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCallAnalysisTagDefinitionRequest }) =>
      updateTagDefinition(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAG_DEFS_KEY });
      addToast({ title: 'Tag updated', variant: 'default' });
    },
    onError: (error: Error) => {
      addToast({
        title: 'Failed to update tag',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useArchiveTagDefinition() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => archiveTagDefinition(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAG_DEFS_KEY });
      addToast({ title: 'Tag archived', variant: 'default' });
    },
    onError: (error: Error) => {
      addToast({
        title: 'Failed to archive tag',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
